<template>
  <section class="investment-page" :dir="pageDirection">
    <div class="page-header">
      <div>
        <h1>{{ tr("pageTitle") }}</h1>
        <p>{{ tr("pageDescription") }}</p>
      </div>
      <span class="currency-badge">
        {{ tr("baseCurrency") }}:
        {{ currencyName }}
        ({{ summary.currency_symbol || "$" }})
        <template v-if="summary.currency_rate">
          — 1$ ≈ {{ formatMoney(summary.currency_rate) }} {{ tr("yer") }}
        </template>
      </span>
    </div>
    <div v-if="loadingSummary" class="summary-state">
      {{ tr("loading") }}
    </div>
    <div v-else-if="summaryError" class="summary-state error">
      {{ summaryError }}
      <button type="button" class="retry-button" @click="fetchBudgetSummary">
        {{ tr("retry") }}
      </button>
    </div>
    <template v-else>
      <div class="kpi-grid">
        <article
          v-for="item in kpiItems"
          :key="item.key"
          class="kpi-card"
          :class="item.className"
        >
          <strong>
            {{ summary.currency_symbol }}{{ formatMoney(item.usd) }}
            <small class="yer-equivalent">
              {{ formatMoney(item.yer) }} {{ tr("yer") }}
            </small>
            <em v-if="item.percent !== null">
              ({{ formatPercent(item.percent) }}%)
            </em>
          </strong>
          <span>{{ item.title }}</span>
          <small>{{ item.description }}</small>
        </article>
      </div>
      <section class="details-card">
        <div class="section-heading">
          <h2>{{ tr("summarySection.title") }}</h2>
          <p>{{ tr("summarySection.description") }}</p>
        </div>
        <div class="summary-table-wrap desktop-only">
          <table class="summary-table">
            <thead>
              <tr>
                <th>{{ tr("summaryTable.item") }}</th>
                <th>{{ tr("summaryTable.usd") }}</th>
                <th>{{ tr("summaryTable.yer") }}</th>
                <th>{{ tr("summaryTable.note") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in summaryRows"
                :key="row.key"
                :class="{ 'summary-total-row': row.total }"
              >
                <td>{{ row.label }}</td>
                <td>
                  {{ summary.currency_symbol }}{{ formatMoney(row.usd) }}
                  <template v-if="row.percent !== null">
                    ({{ formatPercent(row.percent) }}%)
                  </template>
                </td>
                <td>{{ formatMoney(row.yer) }} {{ tr("yer") }}</td>
                <td>{{ row.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="summary-mobile-cards mobile-only">
          <article
            v-for="row in summaryRows"
            :key="`mobile-${row.key}`"
            class="summary-mobile-card"
            :class="{ total: row.total }"
          >
            <h3>{{ row.label }}</h3>
            <div class="mobile-data-row">
              <span>{{ tr("summaryTable.usd") }}</span>
              <strong>
                {{ summary.currency_symbol }}{{ formatMoney(row.usd) }}
                <template v-if="row.percent !== null">
                  ({{ formatPercent(row.percent) }}%)
                </template>
              </strong>
            </div>
            <div class="mobile-data-row">
              <span>{{ tr("summaryTable.yer") }}</span>
              <strong>{{ formatMoney(row.yer) }} {{ tr("yer") }}</strong>
            </div>
            <p>{{ row.note }}</p>
          </article>
        </div>
      </section>
      <section class="details-card facilities-card">
        <div class="section-heading facilities-heading">
          <div>
            <h2>{{ tr("facilitiesSection.title") }}</h2>
            <p>{{ tr("facilitiesSection.description") }}</p>
          </div>
          <div class="table-tools">
            <input
              v-model="search"
              type="search"
              class="search-input"
              :placeholder="tr('searchPlaceholder')"
            />
            <span class="facilities-count">
              {{ filteredFacilities.length }} {{ tr("facilityUnit") }}
            </span>
          </div>
        </div>
        <div v-if="filteredFacilities.length">
          <div class="facilities-table-wrap desktop-only">
            <table class="facilities-table">
              <colgroup>
                <col class="facility-col-index" />
                <col class="facility-col-name" />
                <col class="facility-col-needs" />
                <col class="facility-col-money" />
                <col class="facility-col-money" />
                <col class="facility-col-money" />
                <col class="facility-col-rate" />
                <col class="facility-col-rehab" />
                <col class="facility-col-total" />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{{ tr("facilityTable.facility") }}</th>
                  <th>{{ tr("facilityTable.rehabNeeds") }}</th>
                  <th>{{ tr("facilityTable.current") }}</th>
                  <th>{{ tr("facilityTable.required") }}</th>
                  <th>{{ tr("facilityTable.gap") }}</th>
                  <th>{{ tr("facilityTable.gapRate") }}</th>
                  <th>{{ tr("facilityTable.rehabCost") }}</th>
                  <th>{{ tr("facilityTable.total") }}</th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="facility in filteredFacilities"
                  :key="facility.survey_id"
                >
                  <tr
                    class="facility-row"
                    :class="{ expanded: isFacilityExpanded(facility) }"
                    tabindex="0"
                    role="button"
                    :aria-expanded="isFacilityExpanded(facility)"
                    @click="toggleFacilityDetails(facility)"
                    @keydown.enter.prevent="toggleFacilityDetails(facility)"
                    @keydown.space.prevent="toggleFacilityDetails(facility)"
                  >
                    <td class="survey-id">
                      <button
                        type="button"
                        class="expand-button"
                        :aria-label="detailText.toggleDetails"
                        :aria-expanded="isFacilityExpanded(facility)"
                        @click.stop="toggleFacilityDetails(facility)"
                      >
                        <span class="expand-icon">
                          {{ isFacilityExpanded(facility) ? "−" : "+" }}
                        </span>
                      </button>
                    </td>
                    <td class="facility-name">
                      <strong>{{ facilityDisplayName(facility) }}</strong>
                      <small>{{ facilitySecondaryName(facility) }}</small>
                      <span class="details-link-text">
                        {{ detailText.clickToView }}
                      </span>
                    </td>
                    <td class="rehab-needs">
                      <div
                        v-if="splitNeeds(facility.rehab_need_type).length"
                        class="needs-list"
                      >
                        <span
                          v-for="need in splitNeeds(facility.rehab_need_type)"
                          :key="need"
                          class="need-badge"
                        >
                          {{ normalizeNeed(need) }}
                        </span>
                      </div>
                      <span v-else class="no-data">{{
                        tr("unspecified")
                      }}</span>
                    </td>
                    <td class="money-cell">
                      {{ moneyUsd(facility.current_budget, facility) }}
                      <small>{{
                        moneyYer(facility.current_budget, facility)
                      }}</small>
                    </td>
                    <td class="money-cell">
                      {{ moneyUsd(facility.required_budget, facility) }}
                      <small>{{
                        moneyYer(facility.required_budget, facility)
                      }}</small>
                    </td>
                    <td class="money-cell gap-cell">
                      {{ moneyUsd(facility.budget_gap, facility) }}
                      <small>{{
                        moneyYer(facility.budget_gap, facility)
                      }}</small>
                    </td>
                    <td class="percentage-cell">
                      <span
                        class="gap-badge"
                        :class="gapRateClass(facility.gap_rate)"
                      >
                        {{ formatPercent(facility.gap_rate) }}%
                      </span>
                    </td>
                    <td class="money-cell rehab-cost-cell">
                      {{ moneyUsd(facility.estimated_rehab_cost, facility) }}
                      <small>
                        {{ moneyYer(facility.estimated_rehab_cost, facility) }}
                      </small>
                    </td>
                    <td class="money-cell total-cell">
                      {{ moneyUsd(facility.total_required_cost, facility) }}
                      <small>
                        {{ moneyYer(facility.total_required_cost, facility) }}
                      </small>
                    </td>
                  </tr>
                  <tr
                    v-if="isFacilityExpanded(facility)"
                    class="facility-detail-row"
                  >
                    <td colspan="9">
                      <div class="facility-detail-panel" @click.stop>
                        <div
                          v-if="groupedCostItems(facility).length"
                          class="cost-groups"
                        >
                          <section
                            v-for="group in groupedCostItems(facility)"
                            :key="group.code"
                            class="cost-group"
                          >
                            <div class="cost-group-heading">
                              <div>
                                <span class="group-code">{{ group.code }}</span>
                                <h4>{{ costGroupDisplayName(group) }}</h4>
                              </div>
                              <span class="group-items-count">
                                {{ group.items.length }}
                                {{ detailText.costItems }}
                              </span>
                            </div>
                            <div class="cost-items-table-wrap">
                              <table class="cost-items-table">
                                <colgroup>
                                  <col class="cost-col-item" />
                                  <col class="cost-col-quantity" />
                                  <col class="cost-col-unit" />
                                  <col class="cost-col-price" />
                                  <col class="cost-col-period" />
                                  <col class="cost-col-period" />
                                  <col class="cost-col-period" />
                                  <col class="cost-col-total" />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th>{{ detailText.costItem }}</th>
                                    <th>{{ detailText.quantity }}</th>
                                    <th>{{ detailText.unit }}</th>
                                    <th>{{ detailText.unitPrice }}</th>
                                    <th>{{ detailText.immediate }}</th>
                                    <th>{{ detailText.shortTerm }}</th>
                                    <th>{{ detailText.mediumTerm }}</th>
                                    <th>{{ detailText.threeYearTotal }}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="item in group.items"
                                    :key="`${group.code}-${item.line_no}-${item.item_name_en}`"
                                  >
                                    <td class="cost-item-name">
                                      {{ costItemDisplayName(item) }}
                                    </td>
                                    <td>{{ formatQuantity(item.qty) }}</td>
                                    <td>{{ costItemDisplayUnit(item) }}</td>
                                    <td>
                                      {{ formatMoney(item.unit_rate_usd) }}
                                    </td>
                                    <td>
                                      {{ formatMoney(item.immediate_usd) }}
                                    </td>
                                    <td>
                                      {{ formatMoney(item.short_term_usd) }}
                                    </td>
                                    <td>
                                      {{ formatMoney(item.medium_term_usd) }}
                                    </td>
                                    <td class="item-total">
                                      {{ formatMoney(item.total_usd) }}
                                    </td>
                                  </tr>
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colspan="4">
                                      {{ detailText.subtotal }}
                                    </td>
                                    <td>
                                      {{
                                        formatMoney(group.totals.immediate_usd)
                                      }}
                                    </td>
                                    <td>
                                      {{
                                        formatMoney(group.totals.short_term_usd)
                                      }}
                                    </td>
                                    <td>
                                      {{
                                        formatMoney(
                                          group.totals.medium_term_usd
                                        )
                                      }}
                                    </td>
                                    <td>
                                      {{ formatMoney(group.totals.total_usd) }}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </section>
                        </div>
                        <div v-else class="empty-cost-items">
                          {{ detailText.noCostItems }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3">{{ tr("displayedFacilitiesTotal") }}</td>
                  <td>
                    {{ summary.currency_symbol
                    }}{{ formatMoney(facilitiesTotals.current_budget) }}
                  </td>
                  <td>
                    {{ summary.currency_symbol
                    }}{{ formatMoney(facilitiesTotals.required_budget) }}
                  </td>
                  <td>
                    {{ summary.currency_symbol
                    }}{{ formatMoney(facilitiesTotals.budget_gap) }}
                  </td>
                  <td>{{ formatPercent(facilitiesTotals.gap_rate) }}%</td>
                  <td>
                    {{ summary.currency_symbol
                    }}{{ formatMoney(facilitiesTotals.estimated_rehab_cost) }}
                  </td>
                  <td>
                    {{ summary.currency_symbol
                    }}{{ formatMoney(facilitiesTotals.total_required_cost) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="facility-mobile-cards mobile-only">
            <article
              v-for="facility in filteredFacilities"
              :key="`mobile-${facility.survey_id}`"
              class="facility-mobile-card"
              :class="{ expanded: isFacilityExpanded(facility) }"
            >
              <div
                class="facility-card-head"
                role="button"
                tabindex="0"
                :aria-expanded="isFacilityExpanded(facility)"
                @click="toggleFacilityDetails(facility)"
                @keydown.enter.prevent="toggleFacilityDetails(facility)"
                @keydown.space.prevent="toggleFacilityDetails(facility)"
              >
                <div>
                  <span class="survey-chip">#{{ facility.survey_id }}</span>
                  <h3>{{ facilityDisplayName(facility) }}</h3>
                  <p>{{ facilitySecondaryName(facility) }}</p>
                </div>
                <div class="facility-card-actions">
                  <span
                    class="gap-badge"
                    :class="gapRateClass(facility.gap_rate)"
                  >
                    {{ formatPercent(facility.gap_rate) }}%
                  </span>
                  <span class="mobile-expand-icon">
                    {{ isFacilityExpanded(facility) ? "−" : "+" }}
                  </span>
                </div>
              </div>
              <div class="rehab-mobile-section">
                <span class="field-title">{{
                  tr("facilityTable.rehabNeeds")
                }}</span>
                <div
                  v-if="splitNeeds(facility.rehab_need_type).length"
                  class="needs-list"
                >
                  <span
                    v-for="need in splitNeeds(facility.rehab_need_type)"
                    :key="`m-${need}`"
                    class="need-badge"
                  >
                    {{ normalizeNeed(need) }}
                  </span>
                </div>
                <span v-else class="no-data">{{ tr("unspecified") }}</span>
              </div>
              <div class="facility-card-body">
                <div class="mobile-data-row">
                  <span>{{ tr("facilityTable.current") }}</span>
                  <strong>
                    {{ moneyUsd(facility.current_budget, facility) }}
                    <small>{{
                      moneyYer(facility.current_budget, facility)
                    }}</small>
                  </strong>
                </div>
                <div class="mobile-data-row">
                  <span>{{ tr("facilityTable.required") }}</span>
                  <strong>
                    {{ moneyUsd(facility.required_budget, facility) }}
                    <small>{{
                      moneyYer(facility.required_budget, facility)
                    }}</small>
                  </strong>
                </div>
                <div class="mobile-data-row">
                  <span>{{ tr("facilityTable.gap") }}</span>
                  <strong class="mobile-gap">
                    {{ moneyUsd(facility.budget_gap, facility) }}
                    <small>{{ moneyYer(facility.budget_gap, facility) }}</small>
                  </strong>
                </div>
                <div class="mobile-data-row">
                  <span>{{ tr("facilityTable.rehabCost") }}</span>
                  <strong class="mobile-rehab">
                    {{ moneyUsd(facility.estimated_rehab_cost, facility) }}
                    <small>
                      {{ moneyYer(facility.estimated_rehab_cost, facility) }}
                    </small>
                  </strong>
                </div>
                <div class="mobile-data-row total-row">
                  <span>{{ tr("facilityTable.total") }}</span>
                  <strong>
                    {{ moneyUsd(facility.total_required_cost, facility) }}
                    <small>
                      {{ moneyYer(facility.total_required_cost, facility) }}
                    </small>
                  </strong>
                </div>
                <button
                  type="button"
                  class="mobile-details-button"
                  @click="toggleFacilityDetails(facility)"
                >
                  <span>{{ detailText.clickToView }}</span>
                  <strong>{{
                    isFacilityExpanded(facility) ? "−" : "+"
                  }}</strong>
                </button>
              </div>
              <div
                v-if="isFacilityExpanded(facility)"
                class="mobile-cost-details"
              >
                <div class="mobile-detail-heading">
                  <span>{{ detailText.costBreakdown }}</span>
                  <h4>{{ facilityDisplayName(facility) }}</h4>
                </div>
                <div class="mobile-period-grid">
                  <div>
                    <span>{{ detailText.immediate }}</span>
                    <strong>
                      {{
                        moneyUsd(
                          facilityCostSummary(facility).immediate_usd,
                          facility
                        )
                      }}
                    </strong>
                  </div>
                  <div>
                    <span>{{ detailText.shortTerm }}</span>
                    <strong>
                      {{
                        moneyUsd(
                          facilityCostSummary(facility).short_term_usd,
                          facility
                        )
                      }}
                    </strong>
                  </div>
                  <div>
                    <span>{{ detailText.mediumTerm }}</span>
                    <strong>
                      {{
                        moneyUsd(
                          facilityCostSummary(facility).medium_term_usd,
                          facility
                        )
                      }}
                    </strong>
                  </div>
                  <div class="mobile-period-total">
                    <span>{{ detailText.total }}</span>
                    <strong>
                      {{
                        moneyUsd(
                          facilityCostSummary(facility).total_usd,
                          facility
                        )
                      }}
                    </strong>
                  </div>
                </div>
                <div
                  v-if="groupedCostItems(facility).length"
                  class="mobile-cost-groups"
                >
                  <section
                    v-for="group in groupedCostItems(facility)"
                    :key="`mobile-group-${group.code}`"
                    class="mobile-cost-group"
                  >
                    <div class="mobile-group-heading">
                      <h5>{{ costGroupDisplayName(group) }}</h5>
                      <span
                        >{{ group.items.length }}
                        {{ detailText.costItems }}</span
                      >
                    </div>
                    <article
                      v-for="item in group.items"
                      :key="`mobile-item-${group.code}-${item.line_no}`"
                      class="mobile-cost-item"
                    >
                      <h6>{{ costItemDisplayName(item) }}</h6>
                      <div class="mobile-item-meta">
                        <span>
                          {{ detailText.quantity }}:
                          <strong>{{ formatQuantity(item.qty) }}</strong>
                        </span>
                        <span>
                          {{ detailText.unit }}:
                          <strong>{{ costItemDisplayUnit(item) }}</strong>
                        </span>
                        <span>
                          {{ detailText.unitPrice }}:
                          <strong
                            >${{ formatMoney(item.unit_rate_usd) }}</strong
                          >
                        </span>
                      </div>
                      <div class="mobile-item-periods">
                        <span>
                          {{ detailText.immediate }}
                          <strong
                            >${{ formatMoney(item.immediate_usd) }}</strong
                          >
                        </span>
                        <span>
                          {{ detailText.shortTerm }}
                          <strong
                            >${{ formatMoney(item.short_term_usd) }}</strong
                          >
                        </span>
                        <span>
                          {{ detailText.mediumTerm }}
                          <strong
                            >${{ formatMoney(item.medium_term_usd) }}</strong
                          >
                        </span>
                      </div>
                      <div class="mobile-item-total">
                        <span>{{ detailText.threeYearTotal }}</span>
                        <strong>${{ formatMoney(item.total_usd) }}</strong>
                      </div>
                    </article>
                    <div class="mobile-group-subtotal">
                      <span>{{ detailText.subtotal }}</span>
                      <strong
                        >${{ formatMoney(group.totals.total_usd) }}</strong
                      >
                    </div>
                  </section>
                  <p class="planning-note">{{ detailText.planningNote }}</p>
                </div>
                <div v-else class="empty-cost-items">
                  {{ detailText.noCostItems }}
                </div>
              </div>
            </article>
          </div>
        </div>
        <div v-else class="empty-table">
          {{ tr("noMatchingFacilities") }}
        </div>
      </section>
      <section class="assumptions-card">
        <div class="section-heading">
          <h2>{{ tr("notes.title") }}</h2>
          <p>{{ tr("notes.description") }}</p>
        </div>
        <div class="assumptions-list">
          <div v-for="item in assumptions" :key="item.key" class="assumption">
            <strong>{{ item.title }}</strong>
            <span>{{ item.text }}</span>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";
const { t, locale } = useI18n();
const tr = (key, params = {}) => t(`investmentPage.${key}`, params);
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
const isArabic = computed(() => locale.value === "ar");
const loadingSummary = ref(false);
const summaryError = ref("");
const search = ref("");
const facilities = ref([]);
const expandedFacilityKey = ref(null);
const detailText = computed(() =>
  isArabic.value
    ? {
        toggleDetails: "عرض أو إخفاء تفاصيل تكاليف المرفق",
        clickToView: "اضغط لعرض تفاصيل بنود التكلفة",
        costBreakdown: "التوزيع المرحلي لتكاليف التأهيل",
        detailDescription:
          "تفاصيل بنود التكلفة موزعة على التدخل الفوري والقصير والمتوسط الأجل.",
        close: "إغلاق",
        immediate: "فوري",
        immediatePeriod: "0–3 أشهر",
        shortTerm: "قصير الأجل",
        shortTermPeriod: "3–12 شهرًا",
        mediumTerm: "متوسط الأجل",
        mediumTermPeriod: "1–3 سنوات",
        total: "الإجمالي",
        costItems: "بند تكلفة",
        costItem: "بند التكلفة",
        quantity: "الكمية",
        unit: "الوحدة",
        unitPrice: "سعر الوحدة ($)",
        threeYearTotal: "إجمالي 3 سنوات",
        subtotal: "المجموع الفرعي",
        planningNote:
          "الكميات وأسعار الوحدات تقديرات تخطيطية إرشادية؛ إجماليات البنود هي المرجع.",
        noCostItems: "لا توجد تفاصيل لبنود التكلفة لهذا المرفق.",
      }
    : {
        toggleDetails: "Show or hide facility cost details",
        clickToView: "Click to view cost item details",
        costBreakdown: "Phased rehabilitation cost breakdown",
        detailDescription:
          "Cost items distributed across immediate, short-term, and medium-term interventions.",
        close: "Close",
        immediate: "Immediate",
        immediatePeriod: "0–3 months",
        shortTerm: "Short term",
        shortTermPeriod: "3–12 months",
        mediumTerm: "Medium term",
        mediumTermPeriod: "1–3 years",
        total: "Total",
        costItems: "cost items",
        costItem: "Cost item",
        quantity: "Quantity",
        unit: "Unit",
        unitPrice: "Unit price ($)",
        threeYearTotal: "3-year total",
        subtotal: "Subtotal",
        planningNote:
          "Quantities and unit rates are indicative planning estimates; item totals are authoritative.",
        noCostItems: "No cost item details are available for this facility.",
      }
);
const summary = reactive({
  current_budget: 0,
  required_budget: 0,
  budget_gap: 0,
  gap_rate: 0,
  estimated_rehab_cost: 0,
  total_required_cost: 0,
  current_budget_yer: 0,
  required_budget_yer: 0,
  budget_gap_yer: 0,
  estimated_rehab_cost_yer: 0,
  total_required_cost_yer: 0,
  currency_name: "",
  currency_name_ar: "",
  currency_name_en: "",
  currency_symbol: "$",
  currency_rate: 0,
});
const currencyName = computed(() =>
  isArabic.value
    ? summary.currency_name_ar || summary.currency_name || tr("defaultCurrency")
    : summary.currency_name_en || summary.currency_name || tr("defaultCurrency")
);
const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const normalizeCostItem = (item = {}) => ({
  survey_id: toNumber(item.survey_id),
  health_facility_id: toNumber(item.health_facility_id),
  facility_code: item.facility_code || "",
  group_code: item.group_code || "OTHER",
  group_name_ar: item.group_name_ar || "",
  group_name_en: item.group_name_en || "",
  item_name_ar: item.item_name_ar || "",
  item_name_en: item.item_name_en || "",
  qty: toNumber(item.qty),
  unit_ar: item.unit_ar || "",
  unit_en: item.unit_en || "",
  line_no: toNumber(item.line_no),
  unit_rate_usd: toNumber(item.unit_rate_usd),
  immediate_usd: toNumber(item.immediate_usd),
  short_term_usd: toNumber(item.short_term_usd),
  medium_term_usd: toNumber(item.medium_term_usd),
  total_usd: toNumber(item.total_usd),
});
const normalizeCostSummary = (summaryPayload = {}, costItems = []) => {
  const calculated = costItems.reduce(
    (result, item) => {
      result.immediate_usd += toNumber(item.immediate_usd);
      result.short_term_usd += toNumber(item.short_term_usd);
      result.medium_term_usd += toNumber(item.medium_term_usd);
      result.total_usd += toNumber(item.total_usd);
      return result;
    },
    {
      items_count: costItems.length,
      immediate_usd: 0,
      short_term_usd: 0,
      medium_term_usd: 0,
      total_usd: 0,
    }
  );
  return {
    items_count: toNumber(summaryPayload.items_count) || calculated.items_count,
    immediate_usd:
      toNumber(summaryPayload.immediate_usd) || calculated.immediate_usd,
    short_term_usd:
      toNumber(summaryPayload.short_term_usd) || calculated.short_term_usd,
    medium_term_usd:
      toNumber(summaryPayload.medium_term_usd) || calculated.medium_term_usd,
    total_usd: toNumber(summaryPayload.total_usd) || calculated.total_usd,
  };
};
const facilityKey = (facility) =>
  String(facility?.survey_id || facility?.ref_facility_id || "");
const isFacilityExpanded = (facility) =>
  expandedFacilityKey.value === facilityKey(facility);
const toggleFacilityDetails = (facility) => {
  const key = facilityKey(facility);
  expandedFacilityKey.value = expandedFacilityKey.value === key ? null : key;
};
const facilityCostSummary = (facility) =>
  facility?.cost_items_summary ||
  normalizeCostSummary({}, facility?.cost_items || []);
const groupedCostItems = (facility) => {
  const groups = new Map();
  (facility?.cost_items || []).forEach((item) => {
    const code = item.group_code || "OTHER";
    if (!groups.has(code)) {
      groups.set(code, {
        code,
        name_ar: item.group_name_ar || "",
        name_en: item.group_name_en || "",
        items: [],
        totals: {
          immediate_usd: 0,
          short_term_usd: 0,
          medium_term_usd: 0,
          total_usd: 0,
        },
      });
    }
    const group = groups.get(code);
    group.items.push(item);
    group.totals.immediate_usd += toNumber(item.immediate_usd);
    group.totals.short_term_usd += toNumber(item.short_term_usd);
    group.totals.medium_term_usd += toNumber(item.medium_term_usd);
    group.totals.total_usd += toNumber(item.total_usd);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    items: [...group.items].sort((a, b) => a.line_no - b.line_no),
  }));
};
const costGroupDisplayName = (group) =>
  isArabic.value
    ? group.name_ar || group.name_en || group.code
    : group.name_en || group.name_ar || group.code;
const costItemDisplayName = (item) =>
  isArabic.value
    ? item.item_name_ar || item.item_name_en || "-"
    : item.item_name_en || item.item_name_ar || "-";
const costItemDisplayUnit = (item) =>
  isArabic.value
    ? item.unit_ar || item.unit_en || "-"
    : item.unit_en || item.unit_ar || "-";
const formatQuantity = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(toNumber(value));
const filteredFacilities = computed(() => {
  const value = search.value.trim().toLowerCase();
  if (!value) return facilities.value;
  return facilities.value.filter((facility) =>
    [
      facility.survey_id,
      facility.facility_name_ar,
      facility.facility_name_en,
      facility.rehab_need_type,
      facility.rehab_need_code,
    ].some((field) =>
      String(field ?? "")
        .toLowerCase()
        .includes(value)
    )
  );
});
const facilitiesTotals = computed(() => {
  const totals = filteredFacilities.value.reduce(
    (result, facility) => {
      result.required_budget += toNumber(facility.required_budget);
      result.current_budget += toNumber(facility.current_budget);
      result.budget_gap += toNumber(facility.budget_gap);
      result.estimated_rehab_cost += toNumber(facility.estimated_rehab_cost);
      result.total_required_cost += toNumber(facility.total_required_cost);
      return result;
    },
    {
      required_budget: 0,
      current_budget: 0,
      budget_gap: 0,
      estimated_rehab_cost: 0,
      total_required_cost: 0,
    }
  );
  totals.gap_rate =
    totals.required_budget > 0
      ? (totals.budget_gap / totals.required_budget) * 100
      : 0;
  return totals;
});
const kpiItems = computed(() => [
  {
    key: "current",
    className: "blue",
    usd: summary.current_budget,
    yer: summary.current_budget_yer,
    percent: null,
    title: tr("kpis.current.title"),
    description: tr("kpis.current.description"),
  },
  {
    key: "required",
    className: "amber",
    usd: summary.required_budget,
    yer: summary.required_budget_yer,
    percent: null,
    title: tr("kpis.required.title"),
    description: tr("kpis.required.description"),
  },
  {
    key: "gap",
    className: "red",
    usd: summary.budget_gap,
    yer: summary.budget_gap_yer,
    percent: summary.gap_rate,
    title: tr("kpis.gap.title"),
    description: tr("kpis.gap.description"),
  },
  {
    key: "rehab",
    className: "green",
    usd: summary.estimated_rehab_cost,
    yer: summary.estimated_rehab_cost_yer,
    percent: null,
    title: tr("kpis.rehab.title"),
    description: tr("kpis.rehab.description"),
  },
  {
    key: "total",
    className: "purple",
    usd: summary.total_required_cost,
    yer: summary.total_required_cost_yer,
    percent: null,
    title: tr("kpis.total.title"),
    description: tr("kpis.total.description"),
  },
]);
const summaryRows = computed(() => [
  {
    key: "current",
    label: tr("summaryRows.current.label"),
    usd: summary.current_budget,
    yer: summary.current_budget_yer,
    percent: null,
    note: tr("summaryRows.current.note"),
    total: false,
  },
  {
    key: "required",
    label: tr("summaryRows.required.label"),
    usd: summary.required_budget,
    yer: summary.required_budget_yer,
    percent: null,
    note: tr("summaryRows.required.note"),
    total: false,
  },
  {
    key: "gap",
    label: tr("summaryRows.gap.label"),
    usd: summary.budget_gap,
    yer: summary.budget_gap_yer,
    percent: summary.gap_rate,
    note: tr("summaryRows.gap.note"),
    total: false,
  },
  {
    key: "rehab",
    label: tr("summaryRows.rehab.label"),
    usd: summary.estimated_rehab_cost,
    yer: summary.estimated_rehab_cost_yer,
    percent: null,
    note: tr("summaryRows.rehab.note"),
    total: false,
  },
  {
    key: "total",
    label: tr("summaryRows.total.label"),
    usd: summary.total_required_cost,
    yer: summary.total_required_cost_yer,
    percent: null,
    note: tr("summaryRows.total.note"),
    total: true,
  },
]);
const assumptions = computed(() => [
  {
    key: "currency",
    title: tr("notes.items.currency.title"),
    text: tr("notes.items.currency.text"),
  },
  {
    key: "rate",
    title: tr("notes.items.rate.title"),
    text: tr("notes.items.rate.text", {
      rate: formatMoney(summary.currency_rate),
    }),
  },
  {
    key: "gap",
    title: tr("notes.items.gap.title"),
    text: tr("notes.items.gap.text"),
  },
  {
    key: "gapRate",
    title: tr("notes.items.gapRate.title"),
    text: tr("notes.items.gapRate.text"),
  },
  {
    key: "rehab",
    title: tr("notes.items.rehab.title"),
    text: tr("notes.items.rehab.text"),
  },
  {
    key: "total",
    title: tr("notes.items.total.title"),
    text: tr("notes.items.total.text"),
  },
]);
const fetchBudgetSummary = async () => {
  loadingSummary.value = true;
  summaryError.value = "";
  try {
    const response = await api.get("/reports/rehab-budget-summary");
    const data = response?.data?.data ?? {};
    const summaryPayload = data?.summary ?? {};
    const facilitiesPayload = Array.isArray(data?.facilities)
      ? data.facilities
      : [];
    summary.current_budget = toNumber(summaryPayload.current_budget);
    summary.required_budget = toNumber(summaryPayload.required_budget);
    summary.budget_gap = toNumber(summaryPayload.budget_gap);
    summary.gap_rate = toNumber(summaryPayload.gap_rate);
    summary.estimated_rehab_cost = toNumber(
      summaryPayload.estimated_rehab_cost
    );
    summary.currency_name = summaryPayload.currency_name || "";
    summary.currency_name_ar =
      summaryPayload.currency_name_ar || summaryPayload.currency_name || "";
    summary.currency_name_en =
      summaryPayload.currency_name_en || summaryPayload.currency_name || "";
    summary.currency_symbol = summaryPayload.currency_symbol || "$";
    summary.currency_rate = toNumber(summaryPayload.currency_rate);
    summary.total_required_cost =
      summary.budget_gap + summary.estimated_rehab_cost;
    summary.current_budget_yer = toYer(summary.current_budget);
    summary.required_budget_yer = toYer(summary.required_budget);
    summary.budget_gap_yer = toYer(summary.budget_gap);
    summary.estimated_rehab_cost_yer = toYer(summary.estimated_rehab_cost);
    summary.total_required_cost_yer = toYer(summary.total_required_cost);
    if (summary.gap_rate <= 0 && summary.required_budget > 0) {
      summary.gap_rate = (summary.budget_gap / summary.required_budget) * 100;
    }
    facilities.value = facilitiesPayload.map((facility) => {
      const costItems = Array.isArray(facility.cost_items)
        ? facility.cost_items.map(normalizeCostItem)
        : [];
      return {
        survey_id: toNumber(facility.survey_id),
        ref_facility_id: toNumber(facility.ref_facility_id),
        facility_name_ar: facility.facility_name_ar || "",
        facility_name_en: facility.facility_name_en || "",
        rehab_need_type: facility.rehab_need_type || "",
        rehab_need_code: facility.rehab_need_code || "",
        currency_name: facility.currency_name || summary.currency_name,
        currency_symbol: facility.currency_symbol || summary.currency_symbol,
        currency_rate: toNumber(
          facility.currency_rate || summary.currency_rate
        ),
        required_budget: toNumber(facility.required_budget),
        current_budget: toNumber(facility.current_budget),
        budget_gap: toNumber(facility.budget_gap),
        gap_rate: toNumber(facility.gap_rate),
        estimated_rehab_cost: toNumber(facility.estimated_rehab_cost),
        total_required_cost: toNumber(facility.total_required_cost),
        cost_items_summary: normalizeCostSummary(
          facility.cost_items_summary,
          costItems
        ),
        cost_items: costItems,
      };
    });
  } catch (error) {
    console.error("Failed to load budget summary:", error);
    facilities.value = [];
    summaryError.value = error?.response?.data?.message || tr("loadError");
  } finally {
    loadingSummary.value = false;
  }
};
const toYer = (value) =>
  summary.currency_rate > 0 ? toNumber(value) * summary.currency_rate : 0;
const toFacilityYer = (value, currencyRate) => {
  const rate = toNumber(currencyRate);
  return rate > 0 ? toNumber(value) * rate : 0;
};
const splitNeeds = (value) =>
  value
    ? String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
const normalizeNeed = (value) => {
  const key = String(value || "").trim();
  const map = {
    إنشائي: "structural",
    structural: "structural",
    non_إنشائي: "nonStructural",
    non_structural: "nonStructural",
    "غير إنشائي": "nonStructural",
    "خدمات تشغيلية/طوارئ": "operationalEmergency",
    "خدمات تشغيلية وطوارئ": "operationalEmergency",
    operational_emergency: "operationalEmergency",
    "مياه وصرف صحي": "wash",
    water_sanitation: "wash",
    كهرباء: "electricity",
    electricity: "electricity",
    "معدات طبية": "medicalEquipment",
    medical_equipment: "medicalEquipment",
    "أمن وسلامة": "safety",
    safety: "safety",
    أخرى: "other",
    other: "other",
  };
  return tr(`needs.${map[key] || "other"}`);
};
const gapRateClass = (value) => {
  const rate = toNumber(value);
  if (rate >= 60) return "critical";
  if (rate >= 40) return "high";
  if (rate > 0) return "medium";
  return "none";
};
const formatMoney = (value) =>
  new Intl.NumberFormat(isArabic.value ? "en-US" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(toNumber(value));
const formatPercent = (value) =>
  new Intl.NumberFormat(isArabic.value ? "en-US" : "en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(toNumber(value));
const facilityDisplayName = (facility) =>
  isArabic.value
    ? facility.facility_name_ar || facility.facility_name_en || "-"
    : facility.facility_name_en || facility.facility_name_ar || "-";
const facilitySecondaryName = (facility) =>
  isArabic.value
    ? facility.facility_name_en || "-"
    : facility.facility_name_ar || "-";
const moneyUsd = (value, facility) =>
  `${facility.currency_symbol || summary.currency_symbol}${formatMoney(value)}`;
const moneyYer = (value, facility) =>
  `${formatMoney(toFacilityYer(value, facility.currency_rate))} ${tr("yer")}`;
onMounted(fetchBudgetSummary);
</script>
<style scoped>
.investment-page {
  min-height: 100vh;
  padding: 28px;
  overflow-x: hidden;
  background: #f4f6f9;
  color: #071126;
  font-family: "Inter", "Cairo", sans-serif;
}
.investment-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 22px;
}
.page-header h1 {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 900;
}
.page-header p {
  max-width: 950px;
  margin: 0;
  color: #52657d;
  font-size: 14px;
  line-height: 1.9;
}
.currency-badge {
  flex: 0 0 auto;
  padding: 8px 14px;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
}
.summary-state {
  margin-bottom: 22px;
  padding: 18px;
  color: #334155;
  font-weight: 800;
  text-align: center;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
}
.summary-state.error {
  color: #b91c1c;
  background: #fff7f7;
  border-color: #fecaca;
}
.retry-button {
  display: block;
  margin: 12px auto 0;
  padding: 8px 16px;
  color: #fff;
  font-family: inherit;
  font-weight: 800;
  cursor: pointer;
  background: #143a63;
  border: 0;
  border-radius: 8px;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;
  text-align: center;
}
.kpi-card {
  padding: 18px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-inline-start: 5px solid #143a63;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}
.kpi-card strong {
  display: block;
  margin-bottom: 8px;
  font-size: 23px;
  font-weight: 900;
  line-height: 1.4;
}
.kpi-card strong em {
  display: block;
  color: #dc2626;
  font-size: 15px;
  font-style: normal;
}
.kpi-card > span {
  display: block;
  margin-bottom: 6px;
  color: #061226;
  font-weight: 900;
}
.kpi-card small {
  color: #64748b;
  font-size: 12px;
  line-height: 1.7;
}
.yer-equivalent {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 14px !important;
  font-weight: 800;
}
.kpi-card.amber {
  border-inline-start-color: #d97706;
}
.kpi-card.red {
  border-inline-start-color: #dc2626;
}
.kpi-card.green {
  border-inline-start-color: #16a34a;
}
.kpi-card.purple {
  border-inline-start-color: #7c3aed;
}
.details-card,
.assumptions-card {
  margin-bottom: 22px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}
.section-heading {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.section-heading h2 {
  margin: 0;
  color: #061226;
  font-size: 18px;
  font-weight: 900;
}
.section-heading p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.8;
}
.summary-table-wrap {
  overflow-x: auto;
}
.facilities-table-wrap {
  width: 100%;
  overflow: visible;
}
.summary-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}
.summary-table thead,
.facilities-table thead {
  background: #143a63;
}
.summary-table th,
.facilities-table th {
  padding: 14px;
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  text-align: start;
  white-space: nowrap;
}
.summary-table td {
  padding: 14px;
  font-size: 13px;
  border-bottom: 1px solid #e2e8f0;
}
.summary-table td:nth-child(2),
.summary-table td:nth-child(3) {
  direction: ltr;
  font-weight: 900;
  text-align: center;
}
.summary-total-row {
  background: #f0fdf4;
}
.summary-total-row td {
  color: #15803d;
  font-weight: 900;
}
.facilities-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.table-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}
.search-input {
  width: 280px;
  padding: 10px 13px;
  color: #0f172a;
  font-family: inherit;
  font-size: 13px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 9px;
  outline: none;
}
.search-input:focus {
  border-color: #143a63;
  box-shadow: 0 0 0 3px rgba(20, 58, 99, 0.1);
}
.facilities-count {
  padding: 7px 12px;
  color: #143a63;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  background: #eaf1f8;
  border-radius: 999px;
}
.facilities-table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
}
.facility-col-index {
  width: 5%;
}
.facility-col-name {
  width: 18%;
}
.facility-col-needs {
  width: 21%;
}
.facility-col-money {
  width: 9%;
}
.facility-col-rate {
  width: 7%;
}
.facility-col-rehab {
  width: 10%;
}
.facility-col-total {
  width: 12%;
}
.facilities-table th {
  padding: 12px 6px;
  font-size: 11px;
  line-height: 1.6;
  text-align: center;
  white-space: normal;
}
.facilities-table td {
  padding: 12px 6px;
  color: #334155;
  font-size: 11px;
  vertical-align: middle;
  border-bottom: 1px solid #e2e8f0;
}
.facility-row {
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.facility-row:hover,
.facility-row:focus-visible {
  background: #f5f9fd;
  outline: none;
}
.facility-row.expanded {
  background: #edf5fc;
  box-shadow: inset 4px 0 0 #143a63;
}
.investment-page[dir="rtl"] .facility-row.expanded {
  box-shadow: inset -4px 0 0 #143a63;
}
.expand-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  color: #143a63;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  background: transparent;
  border: 0;
}
.expand-icon,
.mobile-expand-icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 17px;
  font-weight: 900;
  line-height: 1;
  background: #143a63;
  border-radius: 7px;
}
.details-link-text {
  display: inline-block;
  margin-top: 7px;
  color: #2563eb;
  font-size: 10px;
  font-weight: 800;
}
.facility-detail-row > td {
  padding: 0 !important;
  background: #f7fafc;
  border-bottom: 2px solid #cbd5e1 !important;
}
.facility-detail-panel {
  margin: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  box-shadow: 0 14px 35px rgba(15, 35, 64, 0.1);
}
.detail-panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  color: #fff;
  background: linear-gradient(135deg, #0d2f52, #1e527f);
}
.detail-eyebrow {
  display: block;
  margin-bottom: 6px;
  color: #bfdbfe;
  font-size: 11px;
  font-weight: 900;
}
.detail-panel-heading h3 {
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-weight: 900;
}
.detail-panel-heading p {
  margin: 7px 0 0;
  color: #dbeafe;
  font-size: 12px;
  line-height: 1.8;
}
.detail-close-button {
  flex: 0 0 auto;
  padding: 8px 12px;
  color: #fff;
  font-family: inherit;
  font-size: 11px;
  font-weight: 900;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
}
.period-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.period-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-top: 4px solid #2563eb;
  border-radius: 10px;
}
.period-card.short-term {
  border-top-color: #d97706;
}
.period-card.medium-term {
  border-top-color: #7c3aed;
}
.period-card.grand-total {
  background: #f0fdf4;
  border-color: #bbf7d0;
  border-top-color: #16a34a;
}
.period-card span,
.period-card small {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}
.period-card strong {
  display: block;
  margin: 6px 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 900;
  direction: ltr;
  text-align: start;
}
.period-card.grand-total strong {
  color: #15803d;
}
.cost-groups {
  padding: 18px;
}
.cost-group {
  margin-bottom: 18px;
  overflow: hidden;
  border: 1px solid #dbe3ee;
  border-radius: 11px;
}
.cost-group-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 16px;
  background: #edf3f9;
  border-bottom: 1px solid #dbe3ee;
}
.cost-group-heading > div {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cost-group-heading h4 {
  margin: 0;
  color: #0f2f50;
  font-size: 14px;
  font-weight: 900;
}
.group-code,
.group-items-count {
  display: inline-flex;
  padding: 4px 8px;
  color: #143a63;
  font-size: 10px;
  font-weight: 900;
  white-space: nowrap;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}
.cost-items-table-wrap {
  width: 100%;
  overflow: visible;
}
.cost-items-table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
}
.cost-col-item {
  width: 30%;
}
.cost-col-quantity {
  width: 6%;
}
.cost-col-unit {
  width: 10%;
}
.cost-col-price,
.cost-col-period {
  width: 10%;
}
.cost-col-total {
  width: 14%;
}
.cost-items-table thead {
  background: #143a63;
}
.cost-items-table th {
  padding: 10px 6px;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  text-align: center;
  line-height: 1.5;
  white-space: normal;
}
.cost-items-table th:first-child {
  min-width: 0;
  text-align: start;
}
.cost-items-table td {
  padding: 10px 6px !important;
  color: #334155;
  font-size: 11px;
  text-align: center;
  line-height: 1.5;
  white-space: normal;
  background: #fff;
  border-bottom: 1px solid #e2e8f0 !important;
}
.cost-items-table tbody tr:nth-child(even) td {
  background: #fbfdff;
}
.cost-items-table .cost-item-name {
  min-width: 0;
  color: #0f172a;
  font-weight: 800;
  text-align: start;
  white-space: normal;
  line-height: 1.7;
}
.cost-items-table .item-total {
  color: #15803d;
  font-weight: 900;
  background: #f0fdf4 !important;
}
.cost-items-table tfoot {
  background: #eaf1f8;
}
.cost-items-table tfoot td {
  color: #143a63;
  font-weight: 900;
  background: #eaf1f8 !important;
  border-bottom: 0 !important;
}
.cost-items-table tfoot td:first-child {
  text-align: start;
}
.planning-note {
  margin: 0;
  padding: 12px 14px;
  color: #7c2d12;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.8;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 9px;
}
.empty-cost-items {
  margin: 18px;
  padding: 25px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
}
.survey-id {
  color: #143a63 !important;
  font-weight: 900;
  text-align: center;
}
.facility-name {
  min-width: 0;
}
.facility-name strong {
  display: block;
  margin-bottom: 4px;
  color: #061226;
  font-size: 13px;
  font-weight: 900;
}
.facility-name small {
  display: block;
  direction: ltr;
  color: #64748b;
  font-size: 11px;
  text-align: start;
}
.rehab-needs {
  min-width: 0;
}
.needs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.need-badge {
  display: inline-flex;
  padding: 4px 6px;
  color: #334155;
  font-size: 9px;
  font-weight: 800;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 6px;
}
.no-data {
  color: #94a3b8;
  font-size: 11px;
}
.money-cell {
  min-width: 0;
  color: #071126 !important;
  font-weight: 900;
  text-align: center;
  white-space: nowrap;
  direction: ltr;
}
.money-cell small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: 9px;
}
.gap-cell {
  color: #dc2626 !important;
}
.rehab-cost-cell {
  color: #15803d !important;
}
.total-cell {
  color: #15803d !important;
  background: #f0fdf4;
}
.percentage-cell {
  text-align: center;
}
.gap-badge {
  display: inline-flex;
  min-width: 58px;
  justify-content: center;
  padding: 5px 9px;
  font-size: 11px;
  font-weight: 900;
  border-radius: 999px;
}
.gap-badge.critical {
  color: #b91c1c;
  background: #fee2e2;
}
.gap-badge.high {
  color: #c2410c;
  background: #ffedd5;
}
.gap-badge.medium {
  color: #a16207;
  background: #fef3c7;
}
.gap-badge.none {
  color: #475569;
  background: #e2e8f0;
}
.facilities-table tfoot {
  background: #edf3f9;
}
.facilities-table tfoot td {
  color: #143a63;
  font-size: 11px;
  font-weight: 900;
  text-align: center;
  white-space: nowrap;
  border-bottom: 0;
}
.empty-table {
  padding: 35px 20px;
  color: #64748b;
  font-weight: 800;
  text-align: center;
}
.assumptions-list {
  padding: 4px 20px 12px;
}
.assumption {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #e2e8f0;
}
.assumption:last-child {
  border-bottom: 0;
}
.assumption strong {
  color: #143a63;
  font-weight: 900;
}
.assumption span {
  color: #334155;
  font-size: 13px;
  line-height: 1.8;
}
.mobile-only {
  display: none;
}
@media (max-width: 1350px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 1000px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .facilities-heading {
    align-items: stretch;
    flex-direction: column;
  }
  .table-tools {
    justify-content: space-between;
  }
}
@media (max-width: 700px) {
  .investment-page {
    padding: 16px 10px 28px;
  }
  .page-header {
    flex-direction: column;
  }
  .page-header h1 {
    font-size: 22px;
    line-height: 1.5;
  }
  .page-header p {
    font-size: 13px;
  }
  .currency-badge {
    width: 100%;
    box-sizing: border-box;
    white-space: normal;
    line-height: 1.7;
  }
  .kpi-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .kpi-card {
    padding: 15px;
  }
  .kpi-card strong {
    font-size: 21px;
  }
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: grid;
  }
  .summary-mobile-cards {
    gap: 12px;
    padding: 12px;
  }
  .summary-mobile-card {
    padding: 14px;
    background: #fff;
    border: 1px solid #dbe3ee;
    border-radius: 14px;
  }
  .summary-mobile-card.total {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  .summary-mobile-card h3 {
    margin: 0 0 10px;
    color: #061226;
    font-size: 15px;
    font-weight: 900;
  }
  .summary-mobile-card p {
    margin: 10px 0 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.7;
  }
  .table-tools {
    align-items: stretch;
    flex-direction: column;
  }
  .search-input {
    width: 100%;
    box-sizing: border-box;
  }
  .facilities-count {
    align-self: flex-start;
  }
  .facility-mobile-cards {
    gap: 14px;
    padding: 12px;
  }
  .facility-mobile-card {
    overflow: hidden;
    background: #fff;
    border: 1px solid #dbe3ee;
    border-inline-start: 5px solid #143a63;
    border-radius: 16px;
    box-shadow: 0 7px 20px rgba(15, 35, 64, 0.07);
  }
  .facility-mobile-card.expanded {
    border-color: #93b4d4;
    box-shadow: 0 12px 28px rgba(15, 35, 64, 0.13);
  }
  .facility-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    cursor: pointer;
  }
  .facility-card-head:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.25);
    outline-offset: -3px;
  }
  .facility-card-actions {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 9px;
  }
  .mobile-expand-icon {
    width: 28px;
    height: 28px;
  }
  .facility-card-head h3 {
    margin: 8px 0 4px;
    color: #061226;
    font-size: 15px;
    font-weight: 900;
    line-height: 1.7;
  }
  .facility-card-head p {
    margin: 0;
    color: #64748b;
    font-size: 11px;
    direction: ltr;
  }
  .survey-chip {
    display: inline-flex;
    padding: 4px 8px;
    color: #143a63;
    font-size: 11px;
    font-weight: 900;
    background: #eaf1f8;
    border-radius: 7px;
  }
  .rehab-mobile-section {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
  }
  .field-title {
    display: block;
    margin-bottom: 8px;
    color: #64748b;
    font-size: 12px;
    font-weight: 900;
  }
  .facility-card-body {
    padding: 0 14px;
  }
  .mobile-data-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 11px 0;
    border-bottom: 1px dashed #e2e8f0;
  }
  .mobile-data-row:last-child {
    border-bottom: 0;
  }
  .mobile-data-row > span {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
  }
  .mobile-data-row > strong {
    color: #071126;
    font-size: 13px;
    font-weight: 900;
    text-align: end;
    direction: ltr;
  }
  .mobile-data-row strong small {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 10px;
  }
  .mobile-gap {
    color: #dc2626 !important;
  }
  .mobile-rehab {
    color: #15803d !important;
  }
  .total-row {
    margin: 0 -14px;
    padding-inline: 14px;
    background: #f0fdf4;
  }
  .mobile-details-button {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 0 12px;
    padding: 11px 12px;
    color: #143a63;
    font-family: inherit;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
    background: #eaf1f8;
    border: 0;
    border-radius: 9px;
  }
  .mobile-details-button strong {
    display: inline-flex;
    width: 23px;
    height: 23px;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    background: #143a63;
    border-radius: 6px;
  }
  .mobile-cost-details {
    padding: 14px;
    background: #f2f7fb;
    border-top: 2px solid #b8cce0;
  }
  .mobile-detail-heading {
    margin-bottom: 12px;
  }
  .mobile-detail-heading > span {
    color: #2563eb;
    font-size: 10px;
    font-weight: 900;
  }
  .mobile-detail-heading h4 {
    margin: 4px 0 0;
    color: #0f2f50;
    font-size: 15px;
    font-weight: 900;
  }
  .mobile-period-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .mobile-period-grid > div {
    padding: 10px;
    background: #fff;
    border: 1px solid #dbe3ee;
    border-radius: 9px;
  }
  .mobile-period-grid span,
  .mobile-period-grid strong {
    display: block;
  }
  .mobile-period-grid span {
    margin-bottom: 5px;
    color: #64748b;
    font-size: 10px;
    font-weight: 800;
  }
  .mobile-period-grid strong {
    color: #0f172a;
    font-size: 13px;
    font-weight: 900;
    direction: ltr;
    text-align: start;
  }
  .mobile-period-grid .mobile-period-total {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  .mobile-period-total strong {
    color: #15803d;
  }
  .mobile-cost-groups {
    display: grid;
    gap: 12px;
  }
  .mobile-cost-group {
    overflow: hidden;
    background: #fff;
    border: 1px solid #dbe3ee;
    border-radius: 11px;
  }
  .mobile-group-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 12px;
    background: #dfeaf4;
  }
  .mobile-group-heading h5 {
    margin: 0;
    color: #0f2f50;
    font-size: 12px;
    font-weight: 900;
    line-height: 1.6;
  }
  .mobile-group-heading span {
    flex: 0 0 auto;
    padding: 3px 6px;
    color: #143a63;
    font-size: 9px;
    font-weight: 900;
    background: #fff;
    border-radius: 5px;
  }
  .mobile-cost-item {
    margin: 9px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
  }
  .mobile-cost-item h6 {
    margin: 0;
    padding: 10px;
    color: #0f172a;
    font-size: 11px;
    font-weight: 900;
    line-height: 1.7;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  .mobile-item-meta {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    padding: 9px;
  }
  .mobile-item-meta span,
  .mobile-item-periods span {
    color: #64748b;
    font-size: 9px;
  }
  .mobile-item-meta strong,
  .mobile-item-periods strong {
    display: block;
    margin-top: 3px;
    color: #0f172a;
    font-size: 10px;
    font-weight: 900;
  }
  .mobile-item-periods {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    padding: 9px;
    background: #f8fafc;
    border-top: 1px dashed #e2e8f0;
  }
  .mobile-item-total,
  .mobile-group-subtotal {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 10px;
    color: #15803d;
    font-size: 10px;
    font-weight: 900;
    background: #f0fdf4;
    border-top: 1px solid #bbf7d0;
  }
  .mobile-item-total strong,
  .mobile-group-subtotal strong {
    font-size: 12px;
    direction: ltr;
  }
  .mobile-group-subtotal {
    padding: 11px 12px;
    color: #143a63;
    background: #eaf1f8;
    border-color: #cbd5e1;
  }
  .mobile-cost-details .planning-note {
    font-size: 10px;
  }
  .assumption {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
